import { __decorate } from "tslib";
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { shareReplay, catchError, throwError } from 'rxjs';
/**
 * Fetches and caches the list of Cortex applications the current user
 * can access. Designed for extraction into a shared package later.
 */
let CortexAppsService = class CortexAppsService {
    http;
    cache$ = null;
    constructor(http) {
        this.http = http;
    }
    /**
     * Returns the cached list of apps. Only makes one HTTP call per session.
     *
     * The endpoint lives on rv-api; the Angular UI is served from rv-app,
     * so a relative URL resolves to the UI origin and 404s. Prefix with
     * window.appConfig.apiBaseUrl to hit rv-api. Session cookies are
     * shared across `.epicio.localhost` so the authenticated session
     * continues to work.
     *
     * @returns {Observable<CortexAppEntry[]>} Observable of the user's authorized Cortex apps.
     */
    getApps() {
        if (!this.cache$) {
            // `window.appConfig` is declared globally in interfaces/appconfig.ts
            // (`declare global { interface Window { appConfig: AppConfig } }`),
            // so we don't need a cast here. Compose the endpoint via `new URL`
            // so a trailing slash on `apiBaseUrl` (some envs render the
            // ansible value with one, some without) doesn't produce
            // `https://host//api/cortex/apps`. Falling back to
            // `window.location.origin` keeps `new URL` happy when
            // `apiBaseUrl` is empty (relative-origin fetch, same behavior
            // as the previous string concat with an empty prefix).
            const apiBaseUrl = window.appConfig?.apiBaseUrl || window.location.origin;
            const url = new URL('/api/cortex/apps', apiBaseUrl).toString();
            // withCredentials is required: the endpoint is cross-origin
            // (browser on rv, API on rv-api) and authorizes on the rv.sid
            // session cookie. Angular's HttpClient defaults to omitting
            // credentials, so the cookie never ships without this.
            // On error, poison the cache so the NEXT call to getApps() builds a
            // fresh request instead of replaying the cached failure. The error
            // still propagates to subscribers — the component decides how to
            // surface it (leaves `appsLoaded` false so re-opening the switcher
            // retries; renders an empty grid in the meantime).
            const request$ = this.http.get(url, { withCredentials: true });
            this.cache$ = request$.pipe(catchError((err) => {
                this.cache$ = null;
                return throwError(() => err);
            }), shareReplay(1));
        }
        return this.cache$;
    }
    /**
     * Clears the cached apps list (e.g., on logout or session change).
     */
    clearCache() {
        this.cache$ = null;
    }
    static ctorParameters = () => [
        { type: HttpClient }
    ];
};
CortexAppsService = __decorate([
    Injectable({ providedIn: 'root' })
], CortexAppsService);
export { CortexAppsService };
/** @deprecated Use `CortexAppsService`. Retained for in-flight imports. */
export { CortexAppsService as NexusAppsService };
